import * as React from 'react';
import { useContext, useState } from 'react';
import {
  Checkbox,
  Dialog,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { ProductContext } from '../store/productContext';
import useGetProductgroupName from '../utils/hooks/useGetProductgroupName';
import { getRowMenuPosition, type MenuPosition } from '../utils/menuPosition';
import ProductForm from './ProductForm';
import DeleteDialog from '../utils/components/DeleteDialog';

type Props = {
  product: Product;
  loading: boolean;
  selected: boolean;
  onToggleSelect: (productId: number) => void;
};

const ProductRow: React.FC<Props> = ({ product, loading, selected, onToggleSelect }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { getProductgroupName } = useGetProductgroupName();
  const { getProducts, editProduct, editProductLoading, deleteProduct, deleteProductLoading } = useContext(ProductContext);
  const [hovered, setHovered] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const cellSx = () => ({
    p: 1,
    overflow: 'hidden',
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuPosition(getRowMenuPosition(event, '[data-product-row="true"]'));
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
  };

  const handleEditSubmit = (data: ProductFormDto) => {
    editProduct(product.id, data, () => {
      setEditOpen(false);
      getProducts(true);
    });
  };

  const handleDelete = () => {
    deleteProduct(product.id, () => {
      setDeleteOpen(false);
      getProducts(true);
    });
  };

  return (
    <>
      <Grid
        data-product-row="true"
        container
        size={{ xs: 12 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          '&:hover .pr-cell': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Grid className="pr-cell" size={{ xs: 2 }} sx={cellSx()}>
          {loading ? (
            <Skeleton variant="text" />
          ) : (
            <Grid container alignItems="center" wrap="nowrap" sx={{ gap: 1 }}>
              <Checkbox
                checked={selected}
                onChange={() => onToggleSelect(product.id)}
                size="small"
                sx={{ p: 0, color: 'green', '&.Mui-checked': { color: 'green' } }}
              />
              <Typography variant="body2" noWrap>{product.name}</Typography>
            </Grid>
          )}
        </Grid>

        <Grid className="pr-cell" size={{ xs: 2 }} sx={cellSx()}>
          {loading ? <Skeleton variant="text" /> : <Typography variant="body2" noWrap>{product.description}</Typography>}
        </Grid>

        <Grid className="pr-cell" size={{ xs: 2 }} sx={cellSx()}>
          {loading ? <Skeleton variant="text" /> : <Typography variant="body2" noWrap>{getProductgroupName(product.productGroupId)}</Typography>}
        </Grid>

        <Grid className="pr-cell" size={{ xs: 2 }} sx={cellSx()}>
          {loading ? <Skeleton variant="text" /> : <Typography variant="body2" noWrap>{product.dimensions}</Typography>}
        </Grid>

        <Grid className="pr-cell" size={{ xs: 2 }} sx={cellSx()}>
          {loading ? (
            <Skeleton variant="text" />
          ) : (
            <Grid container alignItems="center" wrap="nowrap" sx={{ justifyContent: 'space-between' }}>
              <Typography variant="body2" noWrap>{product.unit}</Typography>
              {hovered && (
                <IconButton size="small" onClick={handleMenuOpen} sx={{ color: 'green', ml: 1 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
          )}
        </Grid>
        <Grid className="pr-cell" size={{ xs: 2 }} sx={cellSx()}>
          {loading ? <Skeleton variant="text" /> : <Typography variant="body2" noWrap>{product.code}</Typography>}
        </Grid>
      </Grid>

      <Menu
        anchorReference="anchorPosition"
        anchorPosition={menuPosition ?? undefined}
        open={Boolean(menuPosition)}
        onClose={handleMenuClose}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        <MenuItem onClick={() => { setEditOpen(true); handleMenuClose(); }}>
          <ListItemIcon>
            <EditIcon fontSize="small" sx={{ color: 'green' }} />
          </ListItemIcon>
          <ListItemText>{t('products:t.edit')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setDeleteOpen(true); handleMenuClose(); }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'green' }} />
          </ListItemIcon>
          <ListItemText>{t('products:t.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <ProductForm
          onSubmit={handleEditSubmit}
          defaultValues={product}
          buttonText={t('common:t.edit')}
          loading={editProductLoading}
          cancelButton
          onCancel={() => setEditOpen(false)}
        />
      </Dialog>

      <DeleteDialog
        loading={deleteProductLoading}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        ns="products"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default ProductRow;
